import React, { forwardRef } from 'react';

import { Fade } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import type { CustomContentProps } from 'notistack';
import { SnackbarContent, SnackbarProvider } from 'notistack';

import { Snackbar } from '../../components/atoms/Snackbar';
import type { Variant as SnackBarVariant } from '../../components/atoms/Snackbar/Snackbar';
import {
  ErrorIcon,
  Info as InfoIcon,
  Success as SuccessIcon,
  Warning as WarningIcon,
} from '../../components/icons';

declare module 'notistack' {
  interface VariantOverrides {
    reward: true;
  }
}

const CustomVariant = forwardRef<HTMLDivElement, CustomContentProps>(
  (props, ref) => (
    <SnackbarContent ref={ref}>
      <Snackbar
        message={props.message as string}
        variant={props.variant as SnackBarVariant}
        isNotification
      />
    </SnackbarContent>
  ),
);
CustomVariant.displayName = 'CustomVariant';

const useStyles = makeStyles((theme: Theme) => ({
  notificationRoot: {
    bottom: '68px', // space for fixed mobile buttons
    maxWidth: 'calc(100% - 16px)',
  },
  notification: {
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.common.white,
    fontFamily: theme.typography.fontFamily,

    '& [class*="contentRoot"]': {
      boxShadow: 'none',
      color: theme.palette.common.white,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.fontSize,
    },

    '& .fd-icon svg': {
      marginRight: theme.spacing(1.5),
    },

    '& .notistack-MuiContent-error': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    },
    '& .notistack-MuiContent-error .fd-icon svg': {
      color: theme.palette.error.contrastText,
    },

    '& .notistack-MuiContent-info': {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.info.contrastText,
    },
    '& .notistack-MuiContent-info .fd-icon svg': {
      color: theme.palette.info.contrastText,
    },

    '& .notistack-MuiContent-success': {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
    '& .notistack-MuiContent-success .fd-icon svg': {
      color: theme.palette.success.contrastText,
    },

    '& .notistack-MuiContent-warning': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
    '& .notistack-MuiContent-warning .fd-icon svg': {
      color: theme.palette.warning.contrastText,
    },
  },

  [theme.breakpoints.up('md')]: {
    notificationRoot: {
      bottom: '0px',
    },
  },
}));

export function NotificationsProvider({ children }): JSX.Element {
  const classes = useStyles();

  return (
    <SnackbarProvider
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom',
      }}
      autoHideDuration={4000}
      classes={{
        containerRoot: classes.notificationRoot,
        root: classes.notification,
      }}
      iconVariant={{
        error: <ErrorIcon />,
        info: <InfoIcon />,
        success: <SuccessIcon />,
        warning: <WarningIcon />,
      }}
      preventDuplicate
      TransitionComponent={Fade}
      Components={{
        reward: CustomVariant,
      }}
    >
      {children}
    </SnackbarProvider>
  );
}
